<template>
  <div>
    <b-card
      title="Ajouter un poids volumétrique"
      aria-label="Formulaire d'ajout d'un poids volumétrique"
    >
      <span class="sr-only">Remplissez ce formulaire pour ajouter un nouveau poids volumétrique utilisé dans la logistique des colis. Tous les champs marqués obligatoires doivent être renseignés.</span>
      <validation-observer ref="simpleRules">
        <b-form
          aria-label="Formulaire d'ajout de poids volumétrique"
          @submit.prevent="addWeight"
        >
          <b-row>
            <b-col
              md="6"
              sm="12"
            >
              <b-form-group
                label-for="weight"
                aria-label="Nom du poids volumétrique"
                description="Saisissez un nom distinctif pour ce poids volumétrique."
              >
                <template #label>
                  Nom du poids volumétrique <span class="text-danger">*</span>
                </template>
                <validation-provider
                  #default="{ errors }"
                  name="poids"
                  rules="required"
                >
                  <b-form-input
                    id="weight"
                    v-model="weight.weight"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Nom du poids volumétrique"
                    aria-label="Entrée du nom du poids volumétrique"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              md="6"
              sm="12"
            >
              <b-form-group
                label-for="unit"
                aria-label="Unité de mesure du poids volumétrique"
                description="Indiquez l'unité de mesure (ex : kg, g, etc.)."
              >
                <template #label>
                  Unité de mesure <span class="text-danger">*</span>
                </template>
                <validation-provider
                  #default="{ errors }"
                  name="unité"
                  rules="required"
                >
                  <b-form-input
                    id="unit"
                    v-model="weight.unit"
                    :state="errors.length > 0 ? false : null"
                    placeholder="kg, g, etc."
                    aria-label="Entrée de l'unité de mesure"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              md="6"
              sm="12"
            >
              <b-form-group
                label-for="min"
                aria-label="Poids minimum autorisé"
                description="Saisissez le poids minimum accepté pour cette catégorie."
              >
                <template #label>
                  Poids minimum <span class="text-danger">*</span>
                </template>
                <validation-provider
                  #default="{ errors }"
                  name="poids minimum"
                  rules="required"
                >
                  <b-form-input
                    id="min"
                    v-model="weight.min"
                    :state="errors.length > 0 ? false : null"
                    type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                    placeholder="Poids minimum"
                    aria-label="Entrée du poids minimum"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              md="6"
              sm="12"
            >
              <b-form-group
                label-for="max"
                aria-label="Poids maximum autorisé"
                description="Saisissez le poids maximum accepté pour cette catégorie."
              >
                <template #label>
                  Poids maximum <span class="text-danger">*</span>
                </template>
                <validation-provider
                  #default="{ errors }"
                  name="poids maximum"
                  rules="required"
                >
                  <b-form-input
                    id="max"
                    v-model="weight.max"
                    :state="errors.length > 0 ? false : null"
                    type="text" inputmode="decimal" :formatter="formatPrice" @keypress="allowOnlyDecimalNumbers"
                    placeholder="Poids maximum"
                    aria-label="Entrée du poids maximum"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              md="12"
              sm="12"
            >
              <b-form-group
                label="Description"
                label-for="description"
                aria-label="Description du poids volumétrique"
                description="Ajoutez une description pour préciser l'usage ou les restrictions de ce poids."
              >
                <b-form-textarea
                  id="description"
                  v-model="weight.description"
                  rows="3"
                  placeholder="Description du poids volumétrique"
                  aria-label="Entrée de la description du poids volumétrique"
                />
              </b-form-group>
            </b-col>

            <b-col
              md="12"
              sm="12"
            >
              <b-form-group aria-label="Statut du poids volumétrique">
                <b-form-checkbox
                  id="is_active"
                  v-model="weight.is_active"
                  name="is_active"
                  switch
                  size="lg"
                  aria-label="Basculer le statut du poids volumétrique"
                >
                  {{ weight.is_active ? 'Actif' : 'Inactif' }}
                </b-form-checkbox>
                <span class="sr-only">Activez ou désactivez ce poids volumétrique pour le rendre disponible ou non dans le système logistique.</span>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <div class="d-flex mt-2">
                <b-button
                  variant="primary"
                  type="submit"
                  class="mr-2"
                  :disabled="isLoading || !isWeightValid"
                  aria-label="Ajouter le poids volumétrique"
                >
                  <div
                    v-if="isLoading"
                    class="d-flex align-items-center"
                  >
                    <b-spinner
                      small
                      class="mr-1"
                    />
                    <span>Traitement en cours...</span>
                  </div>
                  <span v-else>Ajouter</span>
                  <span class="sr-only">Valider l'ajout du poids volumétrique</span>
                </b-button>
                <b-button
                  variant="outline-secondary"
                  :to="{ name: 'weights-list' }"
                  aria-label="Annuler et revenir à la liste des poids volumétriques"
                >
                  Annuler
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

extend('required', {
  validate(value) {
    return {
      required: true,
      valid: ['', null, undefined].indexOf(value) === -1,
    }
  },
  message: 'Ce champ est obligatoire',
  computesRequired: true,
})

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      weight: {
        weight: '',
        min: null,
        max: null,
        unit: 'kg',
        description: '',
        is_active: true,
      },
      isLoading: false,
    }
  },
  computed: {
    isWeightValid() {
      return this.weight.weight.trim() !== ''
    },
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async addWeight() {
      const { simpleRules } = this.$refs
      const isValid = await simpleRules.validate()
      if (isValid) {
        try {
          this.isLoading = true

          // Format numeric fields
          const payload = {
            ...this.weight,
            min: this.weight.min ? parseFloat(this.weight.min) : null,
            max: this.weight.max ? parseFloat(this.weight.max) : null,
          }

          await axios.post('/api/weights/create/', payload)
          // Fix: Use named route instead of raw path
          this.$router.push({ name: 'weight-list' })
          setTimeout(() => {
            this.showToast('success', 'top-center', 'poids ajoutée avec succés')
          }, 1000)
          this.isLoading = false
        } catch (error) {
          setTimeout(() => {
            this.showToast('danger', 'top-center', error.toString())
          }, 1000)
        }
      } else {
        setTimeout(() => {
          this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        }, 1000)
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Consistent form field sizing
.form-control, .vs__dropdown-toggle {
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:focus, &:active, &:hover {
    border-color: #ea5455;
    box-shadow: none;
  }
}

// Textarea exception for height
textarea.form-control {
  height: auto;
}

// Improve v-select styling to match regular inputs
.vs__dropdown-toggle {
  padding: 5px 0;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.vs__selected-options {
  padding: 0 6px;
  align-items: center;
}

.vs__search {
  padding: 5px 8px;
  margin: 0;
}

.vs__actions {
  padding-top: 0;
  padding-bottom: 0;
}

.vs__clear {
  height: 22px;
}

.vs__open-indicator {
  height: 17px;
}
</style>
